import { EmbedSignDocument } from "@documenso/embed-react";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { selectMaybeActiveAccountEid, selectMaybeEmail, selectUserName } from "@/redux/app/appSelectors";
import { toastError } from "@/redux/toasts/toastsReducers";
import {
  useCreateUnsecuredLeaseAgreementMutation,
  useGetOpportunitiesQuery,
  useSubmitUnsecuredLeaseAgreementMutation,
} from "@/shared/api/opportunities";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { CardContent } from "@/shared/components/ui/card";
import { CardDescription } from "@/shared/components/ui/card";
import { CardHeader } from "@/shared/components/ui/card";
import { CardTitle } from "@/shared/components/ui/card";
import { CircularProgress } from "@/shared/components/ui/circular-progress";

const DOCUMENSO_HOST = import.meta.env.VITE_DOCUMENSO_HOST;

type AgreementState = {
  token: string;
  documentId: string;
};

function UnsecuredLeaseOptInView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { leaseInstance: encodedLeaseInstance = "" } = useParams({
    from: "/_authenticated/opportunities/unsecured/$leaseInstance",
  });
  const leaseInstance = decodeURIComponent(encodedLeaseInstance);
  const eid = useSelector(selectMaybeActiveAccountEid);
  const signerEmail = useSelector(selectMaybeEmail);
  const accountName = useSelector(selectUserName);
  const [agreement, setAgreement] = useState<AgreementState | null>(null);
  const [createAgreement, { isLoading: isCreatingAgreement, isError: createAgreementError }] =
    useCreateUnsecuredLeaseAgreementMutation();
  const [submitAgreement, { isLoading: isSubmittingAgreement }] = useSubmitUnsecuredLeaseAgreementMutation();

  const opportunitiesQuery = useGetOpportunitiesQuery(eid || skipToken);
  const isResolvingOpportunity = !eid || opportunitiesQuery.isLoading || opportunitiesQuery.isFetching;
  const opportunity = useMemo(() => {
    if (!opportunitiesQuery.data || !leaseInstance) {
      return null;
    }
    const directMatch = opportunitiesQuery.data[leaseInstance];
    if (directMatch) {
      return directMatch;
    }

    const normalizedLeaseInstance = leaseInstance.toLowerCase();
    const fallbackKey = Object.keys(opportunitiesQuery.data).find(
      (key) => key.toLowerCase() === normalizedLeaseInstance,
    );

    return (fallbackKey ? opportunitiesQuery.data[fallbackKey] : null) ?? null;
  }, [opportunitiesQuery.data, leaseInstance]);

  const resolvedLeaseInstance = opportunity?.lease_instance || leaseInstance;
  const agreementTemplateId = opportunity?.metadata.agreement_template_id?.metadata;

  useEffect(() => {
    if (!eid || !signerEmail || !accountName || !opportunity || !agreementTemplateId || agreement) {
      return;
    }

    createAgreement({
      eid,
      leaseInstance: resolvedLeaseInstance,
      agreementTemplateId,
      signerEmail,
      leaseName: opportunity.xdescr,
      accountName: accountName ?? "",
    })
      .unwrap()
      .then((response) => setAgreement({ token: response.token, documentId: response.documentId }))
      .catch(() =>
        dispatch(
          toastError({
            message: t`Unable to generate the unallocated lease agreement right now. Please try again later.`,
            options: { id: "unsecured-lease-agreement-generate-failed" },
          }),
        ),
      );
  }, [
    agreement,
    agreementTemplateId,
    createAgreement,
    dispatch,
    eid,
    leaseInstance,
    resolvedLeaseInstance,
    opportunity,
    signerEmail,
    accountName,
  ]);

  const handleDocumentCompleted = () => {
    if (!agreement?.documentId || !eid || !signerEmail) {
      return;
    }

    submitAgreement({
      eid,
      leaseInstance: resolvedLeaseInstance,
      documentId: agreement.documentId,
      signerEmail,
    })
      .unwrap()
      .then(() => {
        navigate({ to: "/opportunities", search: { unsecuredLeaseEnrollment: "success" }, replace: true });
      })
      .catch(() =>
        dispatch(
          toastError({
            message: t`We received your signature, but could not finish lease opt-in. Please contact support.`,
            options: { id: "unsecured-lease-agreement-submit-failed" },
          }),
        ),
      );
  };

  if (isResolvingOpportunity) {
    return <CircularProgress className="absolute inset-0 m-auto" />;
  }

  if (!opportunity) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Lease Not Found`}</CardTitle>
          <CardDescription>{t`This lease is no longer available.`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button render={<Link to="/opportunities" />}>{t`Back to Opportunities`}</Button>
        </CardContent>
      </Card>
    );
  }

  if (!agreementTemplateId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Agreement Template Missing`}</CardTitle>
          <CardDescription>{t`This unallocated lease cannot be processed yet because agreement setup is incomplete.`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button render={<Link to="/opportunities" />}>{t`Back to Opportunities`}</Button>
        </CardContent>
      </Card>
    );
  }

  if (createAgreementError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Unable To Start Signing`}</CardTitle>
          <CardDescription>{t`Please try again later or contact support if this keeps happening.`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button render={<Link to="/opportunities" />}>{t`Back to Opportunities`}</Button>
        </CardContent>
      </Card>
    );
  }

  if (!agreement || isCreatingAgreement) {
    return <CircularProgress className="absolute inset-0 m-auto" />;
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2 rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="text-md font-semibold">{t`Unallocated Lease Agreement`}</h2>
        <p className="text-sm text-muted-foreground">
          {isSubmittingAgreement
            ? t`Finalizing your unallocated lease enrollment...`
            : t`Please review and sign your unallocated lease agreement to complete opt-in.`}
        </p>
        <div className="overflow-hidden rounded-md border border-muted [&_iframe]:h-[80vh] [&_iframe]:min-h-[600px]">
          <EmbedSignDocument
            className="w-full"
            token={agreement.token}
            host={DOCUMENSO_HOST}
            onDocumentCompleted={handleDocumentCompleted}
          />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/opportunities/unsecured/$leaseInstance")({
  component: UnsecuredLeaseOptInView,
  context: () => ({ permissions: "opportunities" }),
});
